import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { Suspense, lazy } from "react";
import Index from "./pages/Index";
import { RouteSEO } from "./components/RouteSEO";
import { NewsletterPopup } from "./components/NewsletterPopup";

// Lazy load non-critical pages for bundle splitting
const CompetitiveLanding = lazy(() => import("./pages/CompetitiveLanding"));
const UnlistedPage = lazy(() => import("./pages/UnlistedPage"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TexasPrivacy = lazy(() => import("./pages/TexasPrivacy"));
const SevenCoreSegments = lazy(() => import("./pages/SevenCoreSegments"));
const StoryboardLanding = lazy(() => import("./pages/StoryboardLanding"));
const WorkflowStoryboard = lazy(() => import("./pages/WorkflowStoryboard"));
const DataSource = lazy(() => import("./pages/DataSource"));
const Intent = lazy(() => import("./pages/Intent"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CCPAPrivacy = lazy(() => import("./pages/CCPAPrivacy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DataProcessingAgreement = lazy(() => import("./pages/DataProcessingAgreement"));
const OptOut = lazy(() => import("./pages/OptOut"));
const AcceptableUsePolicy = lazy(() => import("./pages/AcceptableUsePolicy"));
const ServiceLevelAgreement = lazy(() => import("./pages/ServiceLevelAgreement"));
const GDPRRights = lazy(() => import("./pages/GDPRRights"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Analytics = lazy(() => import("./pages/Analytics"));
const DataBrokerRegistration = lazy(() => import("./pages/DataBrokerRegistration"));
const TexasPersonalInjuryCompliance = lazy(() => import("./pages/TexasPersonalInjuryCompliance"));
const VsApollo = lazy(() => import("./pages/VsApollo"));
const FacebookLookalikeGuide = lazy(() => import("./pages/FacebookLookalikeGuide"));
const V4Pixel = lazy(() => import("./pages/V4Pixel"));
const TwinTurboIntent = lazy(() => import("./pages/TwinTurboIntent"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const Capabilities = lazy(() => import("./pages/Capabilities"));
const CapabilitiesOnePager = lazy(() => import("./pages/CapabilitiesOnePager"));
const WhyOurDataIsAccurate = lazy(() => import("./pages/WhyOurDataIsAccurate"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" role="status" aria-label="Loading page" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <GoogleAnalytics />
        <ScrollToTop />
        <RouteSEO />
        <NewsletterPopup />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<V4Pixel />} />
            <Route path="/original" element={<Index />} />
            <Route path="/competitive-analysis" element={<CompetitiveLanding />} />
            <Route path="/unlisted" element={<UnlistedPage />} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/texas-privacy" element={<TexasPrivacy />} />
            <Route path="/7-core-segments" element={<SevenCoreSegments />} />
            <Route path="/use-cases" element={<Navigate to="/7-core-segments" replace />} />
            <Route path="/workflow-builder" element={<StoryboardLanding />} />
            <Route path="/workflow-story" element={<WorkflowStoryboard />} />
            <Route path="/where-the-data-comes-from" element={<DataSource />} />
            <Route path="/how-intent-data-is-gathered" element={<Intent />} />
            {/* Redirect old URLs to new ones */}
            <Route path="/data-source" element={<DataSource />} />
            <Route path="/intent" element={<Intent />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/ccpa-privacy" element={<CCPAPrivacy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/dpa" element={<DataProcessingAgreement />} />
            <Route path="/opt-out" element={<OptOut />} />
            <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
            <Route path="/sla" element={<ServiceLevelAgreement />} />
            <Route path="/gdpr-rights" element={<GDPRRights />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/data-broker-registration" element={<DataBrokerRegistration />} />
            <Route path="/texas-personal-injury-compliance" element={<TexasPersonalInjuryCompliance />} />
            <Route path="/vs-apollo" element={<VsApollo />} />
            <Route path="/facebook-lookalike-audiences" element={<FacebookLookalikeGuide />} />
            <Route path="/v4" element={<Navigate to="/" replace />} />
            <Route path="/twin-turbo-intent" element={<TwinTurboIntent />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/capabilities-onepager" element={<CapabilitiesOnePager />} />
            <Route path="/why-our-data-is-accurate" element={<WhyOurDataIsAccurate />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
